import { createElement, useCallback } from 'react';
import { message } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import wsuiApi from '@api/wsuiApi';
import type { BaseFeedbackParams } from '../types';

interface UseFeedbackApiParams {
    sessionId: string;
}

/**
 * 封装反馈 API 调用的通用逻辑
 * 使用 commonAction 接口（替代旧 like 接口）
 */
export const useFeedbackApi = ({ sessionId }: UseFeedbackApiParams) => {
    /**
     * 调用点赞/点踩/取消 API
     */
    const callFeedbackApi = useCallback(
        async (
            type: 'like' | 'unlike' | 'cancel',
            params: BaseFeedbackParams
        ): Promise<{ success: boolean; error?: string }> => {
            try {
                // 对齐 vita mix 页面 ATTITUDE_MAP：like="1", unlike="2", cancel="3"
                const attitude = type === 'like' ? '1' : type === 'unlike' ? '2' : '3';
                const response = await wsuiApi.commonAction({
                    bizActionType: 'attitude',
                    chatData: { sessionId },
                    bizActionData: {
                        voteMsgInfo: {
                            msgId: params.messageId,
                            attitude,
                            // 对齐 vita mix 页面：reason 传用户手写反馈文本（对齐 callAttitudeApi 第158行）
                            reason: params.content,
                            fragmentId: params.fragmentId,
                            content: params.tags,
                        },
                    },
                    businessType: 'doctor_youyi',
                });

                if (response.errno === 0) {
                    if (type === 'cancel') {
                        message.success({
                            content: '已取消反馈',
                            icon: createElement(CheckCircleFilled, { style: { color: '#00c8c8' } }),
                        });
                    } else if (Number(params.tags?.length) > 0 || Number(params.content?.length) > 0) {
                        message.success({
                            content: type === 'like' ? '感谢您的反馈！' : '感谢您的反馈，我们会继续改进！',
                            icon: createElement(CheckCircleFilled, { style: { color: '#00c8c8' } }),
                        });
                    }
                    return { success: true };
                }

                const errorMsg = response.errmsg || '提交失败，请重试';
                message.error(errorMsg);
                return { success: false, error: errorMsg };
            } catch (error) {
                console.error(`反馈 API 调用失败 (${type}):`, error);
                const errorMsg = '提交失败，请重试';
                message.error(errorMsg);
                return { success: false, error: errorMsg };
            }
        },
        [sessionId]
    );

    return { callFeedbackApi };
};
